import {ReactNode, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {initAlgoliaClient} from "lib/api/algoliaClient";
import {algoliaCredentialsActions, algoliaCredentialsSelectors} from "./algoliaCredentialsSlice";
import {SearchClient, SearchIndex} from "algoliasearch";
import {useAppDispatch} from "lib/redux/hooks";
import {createLoadable, isReady, Loadable} from "lib/Loadable";

export interface WithAlgoliaProps {
    children: ReactNode
}

export function useAlgoliaClient(): Loadable<SearchClient> {
    const [client, setClient] = useState<SearchClient | null>(null);

    const dispatch = useAppDispatch();
    const appId = useSelector(algoliaCredentialsSelectors.appId);
    const apiKey = useSelector(algoliaCredentialsSelectors.apiKey);

    useEffect(() => {
        if (client == null && appId == null && apiKey == null) {
            dispatch(algoliaCredentialsActions.fetchCredentials());
        }
    }, [client, appId, apiKey, dispatch]);

    useEffect(() => {
        if (appId != null && apiKey != null) {
            const c = initAlgoliaClient(appId, apiKey);
            setClient(c);
        }
    }, [appId, apiKey]);

    return useMemo(() => createLoadable(client), [client]);
}

export function useAlgoliaProductIndex(): Loadable<SearchIndex> {
    const [productIndex, setProductIndex] = useState<SearchIndex | null>(null);
    const productIndexName = useSelector(algoliaCredentialsSelectors.productIndex);
    const client = useAlgoliaClient();

    useEffect(() => {
        if (isReady(client) && productIndexName != null) {
            const pi = client.loaded.initIndex(productIndexName);
            setProductIndex(pi);
        }
    }, [client, productIndexName]);

    return useMemo(() => createLoadable(productIndex), [productIndex]);
}